import React from 'react';
import { getPublicQuizStrings } from '@/utils/publicQuizStrings';
import { safeHref } from '@/utils/isAllowedUrl';
import { CIRCLE_SOLID_STYLE } from './chromeIcons';
import type { QuizCloseKind } from './quizHeader';

const CLOSE_CROSS_PATH = 'M1 1l12 12M13 1L1 13'; // i18n-allow: фигура SVG, не надпись

/**
 * Крестик «закрыть» шапки квиза (слово владельца 20.10.2026): справа в шапке,
 * ведёт по адресу автора (`settings.quizHeader.closeUrl`). Без адреса
 * крестика нет — решает разборщик (quizHeader.ts). Пришедшему адресу крестик
 * сам не верит (находка 21.10.2026: адрес ставился мимо двери): `href` — только
 * через `safeHref`, негодный схлопывается в `#`.
 *
 * Живёт в ленивом куске рамки (`quizChromeKit.ts`), а не в первом экране:
 * воронкам без крестика он не стоит ни байта. Пейволл сюда не относится — у
 * него свой `closeMode` (PaywallCloseButton).
 *
 * `circle-solid` — та же форма, что у круглой «назад» (белый круг с тенью,
 * `CIRCLE_SOLID_STYLE` в chromeIcons.tsx, дополнение слова 20.10.2026).
 *
 * `blockNavigation` — канва конструктора: нажатие не уводит автора из
 * редактора. Публичное превью `?preview=1` навигацию не запрещает (шрам
 * WEB-919: isPreview ≠ «навигация запрещена»).
 */
export function QuizCloseButton({
  kind,
  href,
  language,
  blockNavigation,
  iconColor,
}: {
  kind: QuizCloseKind;
  href: string;
  language?: string;
  blockNavigation?: boolean;
  /** Цвет значка круга circle-solid (поле шапки buttonIconColor). */
  iconColor?: string;
}) {
  const circle = kind === 'icon-circle';
  return (
    <a
      href={safeHref(href)}
      target="_self"
      rel="noopener noreferrer"
      data-testid="quiz-close-button"
      data-shape={kind}
      aria-label={getPublicQuizStrings(language).closeLabel}
      onClick={blockNavigation ? (e) => e.preventDefault() : undefined}
      style={
        kind === 'circle-solid'
          ? {
              ...CIRCLE_SOLID_STYLE,
              ...(iconColor ? { color: iconColor } : {}),
            }
          : {
              width: 32,
              height: 32,
              flexShrink: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '50%',
              background: circle
                ? 'var(--color-bg-surface, #fff)'
                : 'transparent',
              borderWidth: circle ? 1 : 0,
              borderStyle: circle ? 'solid' : 'none',
              borderColor: 'var(--color-border-primary, #f0f0f0)',
              boxSizing: 'border-box',
              color: 'var(--color-primary, #1677ff)',
              cursor: 'pointer',
            }
      }
    >
      <svg
        width={14}
        height={14}
        viewBox="0 0 14 14"
        aria-hidden="true"
        focusable="false"
      >
        <path
          d={CLOSE_CROSS_PATH}
          stroke="currentColor"
          strokeWidth={2}
          strokeLinecap="round"
        />
      </svg>
    </a>
  );
}
